import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
const base_url = process.env.REACT_APP_API_URL;

/* HELPERS */
const getMonthString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();

const MatkaCalendar = () => {
  const token = localStorage.getItem("token");
  const { marketId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const marketName = location.state?.marketName || "Matka Market";

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [gameDates, setGameDates] = useState({});
  const [userBalance, setUserBalance] = useState(0);

  /* USER BALANCE (SAME AS MATKALIST) */
  const fetchUserBalance = async () => {
    try {
      const res = await axios.get(`${base_url}/api/matka/user/balance`, {
        headers: {
          Authorization: token,
        },
      });
      if (res.data.success) setUserBalance(res.data.balance || 0);
    } catch {}
  };

  /* FETCH CALENDAR DATA */
  const fetchCalendar = async () => {
    try {
      const month = getMonthString(currentMonth);

      const res = await axios.get(
        `${base_url}/api/admin/matka/game-calendar?market_id=${marketId}&month=${month}`,
      );

      if (res.data.success) {
        const map = {};

        res.data.dates.forEach((d) => {
          const localDate = new Date(d.date).toISOString().split("T")[0];

          map[localDate] = {
            gameId: d.id,
            result: d.result, // null or "32"
          };
        });

        setGameDates(map);
      }
    } catch {
      toast.error("Failed to load calendar");
    }
  };

  useEffect(() => {
    fetchUserBalance();
  }, []);

  useEffect(() => {
    fetchCalendar();
  }, [currentMonth]);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const days = getDaysInMonth(year, month);
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="matka-screen">
      {/* HEADER – SAME AS MATKALIST */}
      <div className="matka-header">
        <div className="matka-header-left">
          <ArrowLeft onClick={() => navigate(-1)} />
          <h3>{marketName}</h3>
        </div>
        <div className="matka-header-right">
          <h3>POINTS: {parseFloat(userBalance).toFixed(2)}</h3>
        </div>
      </div>

      {/* CONTENT */}
      <div className="matka-content calendar p-3">
        {/* MONTH NAV */}
        <div className="calendar-header">
          <button
            className="nav-btn"
            onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}
          >
            <ChevronLeft />
          </button>

          <h4>
            {currentMonth.toLocaleString("default", {
              month: "long",
              year: "numeric",
            })}
          </h4>

          <button
            className="nav-btn"
            onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}
          >
            <ChevronRight />
          </button>
        </div>

        {/* CALENDAR GRID */}
        <div className="calendar-grid">
          {[...Array(days)].map((_, i) => {
            const dateStr = `${year}-${String(month + 1).padStart(
              2,
              "0",
            )}-${String(i + 1).padStart(2, "0")}`;

            const hasGame = Object.prototype.hasOwnProperty.call(
              gameDates,
              dateStr,
            );
            const gameData = gameDates[dateStr];
            const result = gameData?.result;
            const gameId = gameData?.gameId;
            const isToday = dateStr === today;
            const isFuture = dateStr > today;

            return (
              <div
                key={dateStr}
                className={`calendar-cell
                  ${hasGame ? "has-game" : ""}
                  ${result ? "has-result" : ""}
                  ${isToday ? "today" : ""}
                  ${isFuture ? "disabled" : ""}
                `}
                onClick={() => {
                  if (!hasGame || isFuture) return;

                  const slug = marketName.toLowerCase().replace(/\s+/g, "-");

                  navigate(`/matka/${slug}`, {
                    state: {
                      marketId,
                      marketName,
                      gameId,
                      date: dateStr,
                    },
                  });
                }}
              >
                <div className="day">{i + 1}</div>

                {hasGame && (
                  <div
                    className="result-box"
                    title={
                      result !== null ? "Result: " + result : "Result: Pending"
                    }
                  >
                    {result !== null ? result : "P"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* STYLES */}
      <style>{`
      .calendar{
      color:red;
      }
        .calendar-header {
          display:flex;
          justify-content:space-between;
          align-items:center;
          margin-bottom:15px;
        }

        .nav-btn {
          background:#fff;
          border:none;
          box-shadow:0 2px 8px rgba(0,0,0,0.1);
          padding:6px 8px;
          border-radius:8px;
        }

        .calendar-grid {
          display:grid;
          grid-template-columns:repeat(7,1fr);
          gap:10px;
        }

        .calendar-cell {
          background:#fff;
          border-radius:14px;
          padding:10px;
          min-height:85px;
          cursor:pointer;
          box-shadow:0 2px 8px rgba(0,0,0,0.08);
          transition:0.2s;
          text-align:center;
        }

        .calendar-cell:hover {
          transform:translateY(-3px);
        }

        .calendar-cell.disabled {
          opacity:0.35;
          pointer-events:none;
        }

        .calendar-cell.today {
          outline:2px solid #0d6efd;
        }

        .calendar-cell.has-game {
          background:#f6f8ff;
        }

        .calendar-cell.has-result {
          background:#eaffea;
        }

        .day {
          font-weight:700;
          font-size:16px;
          margin-bottom:6px;
        }

        .result-box {
          font-size:13px;
          font-weight:700;
          background:#ff9800;
          color:#fff;
          border-radius:6px;
          padding:4px 6px;
        }
      `}</style>
    </div>
  );
};

export default MatkaCalendar;
